import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleApi } from "../../apis";
import { GET_VOUCHER_VENDORS } from "../../apis/configuration";
import { useSnackbar } from "../../context/SnackbarContext";

const PAGE_LIMIT = 15;

const normalizeVendor = (v, customerPhone) => ({
  company_name: v.company_name || v.vendor_name || v.name || v.biller_name || "-",
  logo_link: v.logo_link || v.icon || v.vendor_icon || null,
  phone: v.customer_phone || v.phone || v.mobile || customerPhone || null,
  vendor_id: v.id,
  recent_transactions: v.transactions?.length
    ? { transactions: v.transactions, has_next: v.has_next || false, next_id: v.next_id || null }
    : null,
});

const useVoucherVendors = ({ customerPhone = null, seedData = null } = {}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const hasSeed = Boolean(seedData?.vendors?.length);
  const seedVendors = hasSeed
    ? seedData.vendors.map((v) => normalizeVendor(v, customerPhone))
    : [];

  const [vendors, setVendors] = useState(hasSeed ? seedVendors : []);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(hasSeed ? Boolean(seedData.has_next) : false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!hasSeed);

  const cancelToken = useRef();

  const fetchVendors = useCallback(async ({ page: p, reset }) => {
    if (!customerPhone) return;

    if (cancelToken.current)
      cancelToken.current.cancel("New request appears, canceling previous request.");
    cancelToken.current = axios.CancelToken.source();

    setIsLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT, customer_phone: customerPhone };

      const data = await handleApi(GET_VOUCHER_VENDORS, params, cancelToken.current, navigate);

      if (data?.success) {
        const incoming = (data.vendors || data.results || []).map((v) => normalizeVendor(v, customerPhone));
        setVendors((prev) => (reset ? incoming : [...prev, ...incoming]));
        setHasNext(Boolean(data.has_next || data.next_page));
        if (reset) {
          setTotalCount(data.total_count || data.count || 0);
        }
      }
    } catch (error) {
      if (error.message === "New request appears, canceling previous request.") return;
      showSnackbar({ message: error?.response?.data?.detail || error?.response?.data?.message || "Error fetching data", severity: "error" });
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [customerPhone, navigate, showSnackbar]);

  const resetAndFetch = useCallback(() => {
    if (!customerPhone) return;
    setVendors([]);
    setPage(1);
    setHasNext(false);
    setIsInitialLoading(true);
    fetchVendors({ page: 1, reset: true });
  }, [customerPhone, fetchVendors]);

  useEffect(() => {
    if (customerPhone && !hasSeed) resetAndFetch();
  }, [customerPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVendors({ page: nextPage, reset: false });
  }, [hasNext, isLoading, page, fetchVendors]);

  return {
    vendors,
    hasNext,
    totalCount,
    isLoading,
    isInitialLoading,
    handleLoadMore,
  };
};

export default useVoucherVendors;
